import React, { useCallback } from "react";
import { Row } from "antd";
import { User } from "/app/src/models";
import Status from "../users/status";
import { useTranslation } from "react-i18next";
import HeroButton from "/app/src/components/HeroUi/Button";
import HeroModal from "/app/src/components/HeroUi/Modal";
import ModalBuilder from "/app/src/components/HeroUi/helpers/ModalBuilder";
import { useDisclosure } from "@heroui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userService } from "/app/src/services";
import { useNavigate } from "react-router-dom";
import {
  useAuthDispatch,
  useAuthState,
  logout,
} from "/app/src/contexts/authentication";

/**
 * Renders the user menu component.
 *
 * @param {Object} props - The properties object.
 * @param {User} props.user - The user object representing the user.
 * @param {Function} props.toggleUserStatus - Function to toggle the user's status.
 * @returns {JSX.Element} The user menu component.
 */
export default function UserMenu({
  user,
  toggleUserStatus,
}: {
  user: User;
  toggleUserStatus: () => void;
}) {
  const navigate = useNavigate();
  const { user: loggedInUser, isAuthenticated } = useAuthState();
  const { t } = useTranslation();
  const dispatch = useAuthDispatch();
  const {
    isOpen: isDisableModalOpen,
    onOpen: openDisableModal,
    onOpenChange: openChangeDisableModal,
  } = useDisclosure();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: openDeleteModal,
    onOpenChange: openChangeDeleteModal,
  } = useDisclosure();
  const queryClient = useQueryClient();
  const { user: authUser } = useAuthState();

  const toggleUser = useCallback(() => {
    toggleUserStatus();
    openChangeDisableModal();
  }, [openChangeDisableModal, toggleUserStatus]);

  const { mutateAsync: deleteUser } = useMutation({
    mutationFn: (id: number) => userService.deleteSingle(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
    },
  });

  const handleLogout = useCallback(() => {
    logout(dispatch);
    navigate("/login");
  }, [dispatch, navigate]);

  const deleteUserHandler = useCallback(() => {
    deleteUser(user.id).then(() => {
      if (authUser.id === user.id) {
        handleLogout();
      } else {
        navigate("/users");
      }
    });
  }, [deleteUser, user.id, authUser.id, navigate, handleLogout]);

  return (
    <div className="pl-[10px]">
      <Row>
        <h4>
          {t("translation:user")} {t("translation:status")}:
        </h4>
      </Row>
      <Row>
        <h2>
          <p>{Status(user.status)}</p>
        </h2>
      </Row>
      <Row>
        {isAuthenticated && loggedInUser?.id === user.id && (
          <HeroButton
            fullWidth
            color="default"
            variant="bordered"
            size="sm"
            onClick={handleLogout}
            className="hover:border-blue-500 hover:text-blue-500 bg-white"
          >
            {t("translation:log_out")}
          </HeroButton>
        )}
        {user.status !== 2 ? (
          <>
            <HeroButton
              fullWidth
              color="default"
              variant="bordered"
              size="sm"
              onClick={openDisableModal}
              className="border-[#a12121] text-[#a12121] bg-white"
            >
              {t("translation:deactivate")} {t("translation:user")}
            </HeroButton>
            <HeroModal
              disableAnimation={false}
              isOpen={isDisableModalOpen}
              onOpenChange={openChangeDisableModal}
              placement="top"
            >
              {ModalBuilder({
                warning: true,
                modalHeader: t("translation:warning"),
                modalBody: t("translation:confirm_deactivate_user"),
                modalFooter: (
                  <>
                    <HeroButton
                      size="sm"
                      color="default"
                      variant="bordered"
                      onClick={openChangeDisableModal}
                      className="hover:border-primary-default hover:text-primary-default"
                    >
                      {t("translation:cancel")}
                    </HeroButton>
                    <HeroButton size="sm" color="red" onClick={toggleUser}>
                      {t("translation:deactivate")}
                    </HeroButton>
                  </>
                ),
              })}
            </HeroModal>
          </>
        ) : (
          <>
            <HeroButton
              fullWidth
              color="primary"
              variant="bordered"
              className="bg-white"
              size="sm"
              onClick={openDisableModal}
            >
              {t("translation:activate")} {t("translation:user")}
            </HeroButton>
            <HeroModal
              disableAnimation={false}
              isOpen={isDisableModalOpen}
              onOpenChange={openChangeDisableModal}
              placement="top"
            >
              {ModalBuilder({
                warning: true,
                modalHeader: t("translation:warning"),
                modalBody: t("translation:confirm_activate_user"),
                modalFooter: (
                  <>
                    <HeroButton
                      size="sm"
                      color="default"
                      variant="bordered"
                      onClick={openChangeDisableModal}
                      className="hover:border-primary-default hover:text-primary-default"
                    >
                      {t("translation:cancel")}
                    </HeroButton>
                    <HeroButton size="sm" color="primary" onClick={toggleUser}>
                      {t("translation:activate")}
                    </HeroButton>
                  </>
                ),
              })}
            </HeroModal>
          </>
        )}
        {user?.status === 2 && (
          <>
            <HeroButton
              fullWidth
              color="default"
              variant="bordered"
              size="sm"
              onClick={openDeleteModal}
              className="border-[#a12121] text-[#a12121] bg-white"
            >
              {t("translation:delete_user")}
            </HeroButton>
            <HeroModal
              disableAnimation={false}
              isOpen={isDeleteModalOpen}
              onOpenChange={openChangeDeleteModal}
              placement="top"
            >
              {ModalBuilder({
                warning: true,
                modalHeader: t("translation:warning"),
                modalBody: t("translation:confirm_delete_user"),
                modalFooter: (
                  <>
                    <HeroButton
                      size="sm"
                      color="default"
                      variant="bordered"
                      onClick={openChangeDeleteModal}
                      className="hover:border-primary-default hover:text-primary-default"
                    >
                      {t("translation:cancel")}
                    </HeroButton>
                    <HeroButton
                      size="sm"
                      color="red"
                      onClick={deleteUserHandler}
                    >
                      {t("translation:delete")}
                    </HeroButton>
                  </>
                ),
              })}
            </HeroModal>
          </>
        )}
      </Row>
    </div>
  );
}
